import { useModules, useModuleStepLog } from '@ctcells/api'
import { dataSourceFor } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import { useMemo } from 'react'
import { useStreamState } from '@ctcells/realtime'

import { EmptyState } from '../../atoms/EmptyState.tsx'
import { StripEventsTable } from '../../shell/StripEvents.tsx'
import { stripEventRows, type ModuleEventName } from '../../shell/stripEventsModel.tsx'
import { DEMO_MODULES } from '../demoData.ts'
import { findModule, monitoringDeviceName, type MonitoringDeviceKey } from './monitoringModel.ts'

import './moduleEventLog.css'

/** 모듈 화면은 자기 이벤트, 통합 콘솔은 네 장비(운송 로봇 포함 · eric 2026-11-02 「4개의 장비에서」) 전체. 직접 실행에는 batch_id가 없어도 된다. */
export function ModuleEventLog({ device }: { device: MonitoringDeviceKey | 'all' }) {
  const live = dataSourceFor('modules') === 'live'
  const modules = useModules({ skip: !live })
  const snapshots = live ? (modules.data?.items ?? []) : DEMO_MODULES
  const centId = findModule(snapshots, 'cent')?.module_id
  const asmId = findModule(snapshots, 'asm')?.module_id
  const stainId = findModule(snapshots, 'stain')?.module_id
  const robotId = findModule(snapshots, 'robot')?.module_id
  const cent = useModuleStepLog(device === 'all' || device === 'cent' ? centId : undefined)
  const asm = useModuleStepLog(device === 'all' || device === 'asm' ? asmId : undefined)
  const stain = useModuleStepLog(device === 'all' || device === 'stain' ? stainId : undefined)
  const robot = useModuleStepLog(device === 'all' || device === 'robot' ? robotId : undefined)
  const steps = useMemo(() => [...cent, ...asm, ...stain, ...robot], [cent, asm, stain, robot])
  const moduleNames = useMemo(() => {
    if (device !== 'all') return undefined
    const names = new Map<string, ModuleEventName>()
    const put = (id: string | undefined, key: MonitoringDeviceKey) => {
      if (id !== undefined)
        names.set(id, {
          name: monitoringDeviceName(key),
          short: t(`monitoring.deviceSelect.short.${key}`),
        })
    }
    put(centId, 'cent')
    put(asmId, 'asm')
    put(stainId, 'stain')
    put(robotId, 'robot')
    return names
  }, [device, centId, asmId, stainId, robotId])
  const stale = useStreamState('events').stale
  const rows = stripEventRows([], steps, 'all')
  return (
    <section
      className={device === 'all' ? 'module-events module-events--all' : 'module-events'}
      aria-label={t('card.batchDetail.events')}
    >
      <h3 className="module-events__title">{t('card.batchDetail.events')}</h3>
      <div className="strip-events">
        {stale ? (
          <p className="strip-events__stale" role="status">
            {t('strip.eventsStale')}
          </p>
        ) : null}
        {rows.length === 0 ? (
          <div className="module-events__empty">
            <EmptyState label={t('state.empty.events')} />
          </div>
        ) : (
          <StripEventsTable
            rows={rows}
            showStepProgress={false}
            moduleNames={moduleNames}
            highlightLatest
          />
        )}
      </div>
    </section>
  )
}
